import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  Stack,

  Zoom,
  IconButton,
  Tooltip
} from '@mui/material';
import AccessTime from '@mui/icons-material/AccessTime';
import {
  Block,
  GppBad,
  Home,
  Info,
  Close
} from '@mui/icons-material';
import CustomCard from '../../components/common/CustomCard';
import CustomSnackbar from '../../components/common/CustomSnackbar';

// Mapping of reasons to content
const REASON_MAP = {
  expired_link: {
    title: 'Link Expired',
    text: 'The link you are trying to access is no longer valid. This may happen when the interview link has expired or been closed.',
    chip: { label: 'Expired Link', color: 'warning', icon: <GppBad /> }
  },
  expired_setup_account_link: {
    title: 'Set up Account Link Expired',
    text: 'The link you used to set up your account is no longer active. Please contact your Administrator to generate a new link.',
    chip: { label: 'Expired Link', color: 'warning', icon: <GppBad /> }
  },
  already_completed: {
    title: 'Interview Already Completed',
    text: 'Our system shows that you have already completed this interview. Multiple attempts are not allowed.',
    chip: { label: 'Already Attempted', color: 'error', icon: <Block /> }
  },
  tab_switch_exceeded: {
    title: 'Interview Terminated - Tab Switching',
    text: 'You have switched tabs too many times during the interview. For the integrity of the assessment, the interview has been terminated. Multiple tab switches are not allowed.',
    chip: { label: 'Tab Switch Limit Exceeded', color: 'error', icon: <Block /> }
  },
  inactive_long_time: {
    title: 'Session Inactive',
    text: 'Your interview session was closed due to inactivity for more than 4 minutes. Please contact the recruiter or use a new interview link to continue.',
    chip: { label: 'Session Inactive', color: 'warning', icon: <AccessTime /> }
  },
  default: {
    title: 'Access Restricted',
    text: 'You are not authorized to access this page.',
    chip: { label: 'Restricted', color: 'error', icon: <Block /> }
  }
};

const AccessRestricted = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const reason = location.state?.reason || 'default';
  const reasonInfo = REASON_MAP[reason] || REASON_MAP.default;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSnackbar({
        open: true,
        message: reasonInfo.text,
        severity: reasonInfo.chip.color === 'warning' ? 'warning' : 'error'
      });
    }, 600);
    return () => clearTimeout(timer);
  }, [reasonInfo]);

  useEffect(() => {
    const handleBack = () => {
      window.location.href = "/";
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, []);

  const handleGoHome = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  const handleContactSupport = useCallback(() => {
    window.location.href = '/support';
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #dfe4ea 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Container maxWidth="lg">
        <CustomCard elevation={6} size={{ height: '100%', width: '100%' }}>
          <Box sx={{ textAlign: 'center', p: 4 }}>

            {/* {showAlert && (
                <Zoom in timeout={500}>
                  <Alert
                    severity={reasonInfo.chip.color}
                    sx={{ mb: 3, borderRadius: 2 }}
                    action={
                      <IconButton size="small" onClick={() => setShowAlert(false)}>
                        <Close fontSize="inherit" />
                      </IconButton>
                    }
                  >
                    <AlertTitle>{reasonInfo.title}</AlertTitle>
                    {reasonInfo.text}
                  </Alert>
                </Zoom>
              )} */}

            {/* Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: `${reasonInfo.chip.color}.light`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto',
                boxShadow: `0 8px 24px rgba(0,0,0,0.08)`
              }}
            >
              {reasonInfo.chip.icon}
            </Box>

            {/* Title */}
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              {reasonInfo.title}
            </Typography>

            {/* Description */}
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {reasonInfo.text}
            </Typography>

            {/* Reason Chip */}
            <Chip
              icon={reasonInfo.chip.icon}
              label={reasonInfo.chip.label}
              color={reasonInfo.chip.color}
              variant="outlined"
              sx={{ mb: 3 }}
            />

            {/* Actions
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Tooltip title="Go to homepage">
                  <Button
                    variant="contained"
                    startIcon={<Home />}
                    onClick={handleGoHome}
                    sx={{
                      py: 1.3,
                      background: 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
                      '&:hover': { background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }
                    }}
                  >
                    Home
                  </Button>
                </Tooltip>

                <Tooltip title="Contact our support team">
                  <Button
                    variant="outlined"
                    startIcon={<Info />}
                    onClick={handleContactSupport}
                  >
                    Contact Support
                  </Button>
                </Tooltip>
              </Stack> */}

            <Divider sx={{ my: 3 }} />

            {/* Info */}
            <Typography variant="caption" display="block">
              🔒 All interview activities are monitored to ensure fairness and integrity.
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              If you believe this is an error, please reach out to our support team.
            </Typography>
          </Box>
        </CustomCard>
      </Container>

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </Box >
  );
};

export default AccessRestricted;